import { Operation } from "@/pages/part/operationList/lib/types";
import { QuoteMessage } from "./types";
import type { components } from "@/lib/ApiSchema";

export async function fetchRfq(
  teamSlug: string,
  rfqId: string,
): Promise<components["schemas"]["RfqFullSchema"]> {
  return _fetch(`teams/${teamSlug}/rfqs/${rfqId}/full`, "GET");
}

export async function deleteRfq(
  teamSlug: string,
  rfqId: string,
): Promise<unknown> {
  return _fetch(`teams/${teamSlug}/rfqs/${rfqId}`, "DELETE");
}

export async function fetchSuggestOperations(
  teamSlug: string,
  requested_part_rev_id: string,
): Promise<components["schemas"]["OperationSummarySchema"][]> {
  return _fetch(
    `teams/${teamSlug}/requested_parts/${requested_part_rev_id}/suggest_operations`,
    "GET",
  );
}

export async function moveOperation(
  teamSlug: string,
  id: string,
  ordinal: string,
): Promise<Operation> {
  return _fetch(`teams/${teamSlug}/operations/${id}`, "PATCH", { ordinal });
}

export async function createOperation(
  teamSlug: string,
  partId: string,
  name: string,
  type: string,
  draft: object,
  ordinal: string,
): Promise<Operation> {
  return _fetch(`teams/${teamSlug}/operations`, "POST", {
    requested_part_rev_id: partId,
    name,
    ordinal,
    draft,
    type,
  });
}

export async function deleteOperation(
  teamSlug: string,
  operationId: string,
): Promise<unknown> {
  return _fetch(`teams/${teamSlug}/operations/${operationId}`, "DELETE");
}

export async function confirmCostEstimates(
  teamSlug: string,
  quoteRevisionId: string,
): Promise<boolean> {
  return _fetch(
    `teams/${teamSlug}/quote_revisions/${quoteRevisionId}/confirm_cost_estimates`,
    "POST",
  );
}

export async function fetchQuoteMessage(
  teamSlug: string,
  quoteRevisionId: string,
): Promise<QuoteMessage> {
  return _fetch(
    `teams/${teamSlug}/quote_revisions/${quoteRevisionId}/reply_message`,
    "GET",
  );
}

export async function updateQuoteMessage(
  teamSlug: string,
  quoteRevisionId: string,
  message: string,
): Promise<unknown> {
  return _fetch(
    `teams/${teamSlug}/quote_revisions/${quoteRevisionId}/reply_message`,
    "PUT",
    { body: message },
  );
}

export async function addOrganization(
  teamSlug: string,
  name: string,
  email_domain: string,
  accounting_system_organization_id: string | null,
): Promise<null> {
  return await _fetch(`teams/${teamSlug}/organizations`, "POST", {
    name,
    email_domain,
    accounting_system_organization_id,
  });
}

export async function editOrganization(
  teamSlug: string,
  id: string,
  name: string,
  email_domain: string,
  accounting_system_organization_id: string | null,
): Promise<components["schemas"]["OrganizationDetailSchema"]> {
  return await _fetch(`teams/${teamSlug}/organizations/${id}`, "PATCH", {
    name,
    email_domain,
    accounting_system_organization_id,
  });
}

export async function deleteOrganization(
  teamSlug: string,
  id: string,
): Promise<null> {
  return await _fetch(`teams/${teamSlug}/organizations/${id}`, "DELETE");
}

export async function addNote(
  teamSlug: string,
  rfqId: string,
  content: string,
): Promise<null> {
  return await _fetch(`teams/${teamSlug}/rfqs/${rfqId}/add_note`, "POST", {
    content,
  });
}

export async function _fetch(path: string, method: string, body?: unknown) {
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  };

  const res = await fetch(`/api/${path}`, options);
  if (!res.ok) {
    const json = await res.json();
    throw new Error(JSON.stringify(json));
  } else if (res.status === 200) {
    const json = await res.json();
    const lastTransactionId = res.headers.get("last-transaction-id");
    if (lastTransactionId) {
      json.lastTransactionId = BigInt(lastTransactionId);
    }
    return json;
  }
  return null;
}
